/**
 * Utility per l'HTML delle newsletter:
 * - placeholder dinamici supportati (§11.5)
 * - vincolo unsubscribe obbligatorio
 * - inlining stili email-safe lato FE (DIP-4) tramite `juice` (browser build = client.js)
 *
 * Riferimento: TDD Sprint 18 §8.3.2 / §8.3.3.
 */
import juice from 'juice';

/** Placeholder dinamici inseribili nel corpo/oggetto. */
export const PLACEHOLDERS = [
  { token: '{{nome}}', label: 'Nome' },
  { token: '{{cognome}}', label: 'Cognome' },
  { token: '{{email}}', label: 'Email' },
  { token: '{{entita}}', label: 'Entità (sigla)' },
  // Dati del mittente, presi dall'anagrafica dell'entità: restano vuoti se non compilati.
  { token: '{{entita_nome}}', label: 'Entità (nome completo)' },
  { token: '{{entita_indirizzo}}', label: 'Indirizzo sede' },
  { token: '{{entita_contatti}}', label: 'Recapiti sede' },
];

/**
 * Segnaposto del template che indica dove va il contenuto della newsletter.
 * Un template che lo contiene AVVOLGE la newsletter: l'admin scrive solo il contenuto e,
 * all'invio, il BE lo inserisce nel template (a sua volta dentro la cornice fissa).
 * Senza segnaposto il template viene copiato nel corpo, come in origine.
 */
export const CONTENT_PLACEHOLDER = '{{contenuto}}';

/** @param {string} html @returns {boolean} */
export function hasContentPlaceholder(html) {
  return typeof html === 'string' && html.includes(CONTENT_PLACEHOLDER);
}

/** Placeholder obbligatorio lato BE: viene iniettato automaticamente se assente (vedi ensureUnsubscribePlaceholder). */
export const UNSUBSCRIBE_PLACEHOLDER = '{{unsubscribe_url}}';

/** Attributo che marca il footer di disiscrizione iniettato automaticamente (per poterlo nascondere nell'editor). */
export const UNSUBSCRIBE_MARKER = 'data-nl-unsubscribe';

/**
 * Footer di disiscrizione di default, aggiunto automaticamente quando il corpo non lo contiene.
 * Include due opzioni:
 *  - {{unsubscribe_url}}         → disiscrizione da TUTTE le comunicazioni (obbligatorio lato BE)
 *  - {{unsubscribe_entity_url}}  → disiscrizione dalla SOLA entità mittente
 */
export const DEFAULT_UNSUBSCRIBE_HTML =
  `<p ${UNSUBSCRIBE_MARKER}="1" style="font-size:12px;color:#888888;text-align:center;margin-top:24px">` +
  'Non vuoi più ricevere queste comunicazioni? ' +
  '<a href="{{unsubscribe_url}}" style="color:#888888">Disiscriviti da tutte</a>' +
  ' oppure ' +
  '<a href="{{unsubscribe_entity_url}}" style="color:#888888">solo da questo mittente</a>.' +
  '</p>';

/**
 * Voci aggiuntive del menu "Inserisci campo" nell'editor dei TEMPLATE: blocchi che hanno
 * senso solo lì. Un template con lo spazio contenuto deve avere anche la disiscrizione
 * (il corpo della newsletter, in quel caso, non la contiene).
 */
export const TEMPLATE_INSERTS = [
  {
    label: 'Spazio contenuto newsletter',
    secondary: CONTENT_PLACEHOLDER,
    // In un paragrafo a sé: il BE sostituisce l'intero paragrafo con il contenuto.
    content: `<p>${CONTENT_PLACEHOLDER}</p>`,
  },
  { label: 'Link di disiscrizione', secondary: UNSUBSCRIBE_PLACEHOLDER, content: DEFAULT_UNSUBSCRIBE_HTML },
];

/**
 * Verifica la presenza del placeholder di disiscrizione globale.
 * @param {string} html
 * @returns {boolean}
 */
export function hasUnsubscribePlaceholder(html) {
  return typeof html === 'string' && html.includes(UNSUBSCRIBE_PLACEHOLDER);
}

/**
 * Garantisce la presenza del placeholder di disiscrizione obbligatorio:
 * se assente, accoda il footer di default (marcato). L'utente non deve occuparsene manualmente.
 * @param {string} html
 * @returns {string}
 */
export function ensureUnsubscribePlaceholder(html) {
  const safe = html || '';
  if (hasUnsubscribePlaceholder(safe)) return safe;
  return `${safe}${DEFAULT_UNSUBSCRIBE_HTML}`;
}

/**
 * Rimuove dal corpo il paragrafo del footer di disiscrizione AUTO-INIETTATO, così non
 * viene mostrato nell'editor di composizione. Resta presente nell'email inviata
 * (viene reiniettato al salvataggio da ensureUnsubscribePlaceholder).
 *
 * Riconosce il footer in modo robusto — anche su contenuti salvati prima dell'introduzione
 * del marker o passati per TipTap (che può rimuovere l'attributo) — rimuovendo il <p> che:
 *   - ha l'attributo marker, OPPURE
 *   - contiene il placeholder {{unsubscribe_url}}, OPPURE
 *   - contiene il testo noto del footer di default.
 * @param {string} html
 * @returns {string}
 */
export function stripUnsubscribeBlock(html) {
  if (!html) return html;
  return html
    .replace(/<p\b[^>]*>[\s\S]*?<\/p>/gi, (block) => {
      const isFooter =
        block.includes(`${UNSUBSCRIBE_MARKER}="1"`) ||
        block.includes(UNSUBSCRIBE_PLACEHOLDER) ||
        /non vuoi più ricevere queste comunicazioni/i.test(block);
      return isFooter ? '' : block;
    })
    .trim();
}

/**
 * Porta gli stili inline per rendere l'HTML compatibile con i client email.
 * Il backend NON esegue inlining (PA-7): è a carico del FE/template.
 * In caso di errore di inlining, ritorna l'HTML originale (fail-safe, non blocca l'invio).
 * @param {string} html
 * @returns {string}
 */
export function inlineEmailHtml(html) {
  if (!html) return html;
  try {
    return juice(html);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Inlining HTML newsletter fallito, uso HTML non inlined:', e);
    return html;
  }
}

export default {
  PLACEHOLDERS,
  UNSUBSCRIBE_PLACEHOLDER,
  UNSUBSCRIBE_MARKER,
  DEFAULT_UNSUBSCRIBE_HTML,
  hasUnsubscribePlaceholder,
  ensureUnsubscribePlaceholder,
  stripUnsubscribeBlock,
  inlineEmailHtml,
};